import type { ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";

const SAFE_HREF = /^https?:\/\//i;

function previewUrlTransform(url: string): string {
  const trimmed = url.trim();
  return SAFE_HREF.test(trimmed) ? trimmed : "";
}

function MarkdownLink({
  href,
  children,
}: {
  href?: string;
  children?: ReactNode;
}) {
  if (!href || !SAFE_HREF.test(href)) {
    return <Box component="span">{children}</Box>;
  }
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
  );
}

const headingSx = {
  fontWeight: 600,
  color: "#0D0D0D",
  m: 0,
  lineHeight: 1.4,
} as const;

const bodySx = {
  fontSize: 14,
  color: "#404040",
  lineHeight: 1.6,
  m: 0,
} as const;

const cellSx = {
  border: "1px solid #E5E7EB",
  px: 1,
  py: 0.75,
  verticalAlign: "top",
  textAlign: "left",
  ...bodySx,
} as const;

const components: Components = {
  h1: ({ children }) => (
    <Typography component="h2" sx={{ ...headingSx, fontSize: 18 }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography component="h2" sx={{ ...headingSx, fontSize: 16 }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography component="h3" sx={{ ...headingSx, fontSize: 15 }}>
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography component="h4" sx={{ ...headingSx, fontSize: 14 }}>
      {children}
    </Typography>
  ),
  h5: ({ children }) => (
    <Typography component="h5" sx={{ ...headingSx, fontSize: 14 }}>
      {children}
    </Typography>
  ),
  h6: ({ children }) => (
    <Typography component="h6" sx={{ ...headingSx, fontSize: 13 }}>
      {children}
    </Typography>
  ),
  p: ({ children }) => <Typography sx={bodySx}>{children}</Typography>,
  ul: ({ children }) => (
    <Box
      component="ul"
      sx={{
        m: 0,
        pl: 3,
        listStyleType: "disc",
        listStylePosition: "outside",
        "& > li:not(:last-child)": { mb: 0.5 },
      }}
    >
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box
      component="ol"
      sx={{
        m: 0,
        pl: 3,
        listStyleType: "decimal",
        listStylePosition: "outside",
        "& > li:not(:last-child)": { mb: 0.5 },
      }}
    >
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" sx={{ ...bodySx, display: "list-item" }}>
      {children}
    </Typography>
  ),
  a: MarkdownLink,
  blockquote: ({ children }) => (
    <Box
      component="blockquote"
      sx={{
        m: 0,
        pl: 1.5,
        borderLeft: "3px solid #D0D5DD",
        color: "#404040",
      }}
    >
      {children}
    </Box>
  ),
  hr: () => (
    <Box component="hr" sx={{ border: 0, borderTop: "1px solid #E5E7EB", m: 0 }} />
  ),
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        overflowX: "auto",
        bgcolor: "#F3F4F6",
        borderRadius: "6px",
        fontSize: 13,
        lineHeight: 1.5,
        "& code": { bgcolor: "transparent", p: 0, fontSize: "inherit" },
      }}
    >
      {children}
    </Box>
  ),
  code: ({ children }) => (
    <Box
      component="code"
      sx={{
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "0.9em",
        bgcolor: "#F3F4F6",
        px: 0.5,
        borderRadius: "3px",
      }}
    >
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <Box sx={{ overflowX: "auto" }}>
      <Box
        component="table"
        sx={{
          borderCollapse: "collapse",
          width: "max-content",
          minWidth: "100%",
          fontSize: 13,
        }}
      >
        {children}
      </Box>
    </Box>
  ),
  th: ({ children }) => (
    <Box
      component="th"
      sx={{
        ...cellSx,
        fontWeight: 600,
        color: "#101828",
        bgcolor: "#F9FAFB",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  ),
  td: ({ children }) => (
    <Box component="td" sx={cellSx}>
      {children}
    </Box>
  ),
  img: ({ src, alt }) => {
    if (!src || !SAFE_HREF.test(src)) return null;
    return (
      <Box
        component="img"
        src={src}
        alt={alt ?? ""}
        sx={{ maxWidth: "100%", height: "auto" }}
      />
    );
  },
  input: ({ checked, type }) => (
    <Box
      component="input"
      type={type}
      checked={Boolean(checked)}
      disabled
      readOnly
      sx={{ mr: 0.75 }}
    />
  ),
};

type MarkdownContentProps = Readonly<{
  markdown: string | null | undefined;
  emptyLabel?: string;
}>;

export default function MarkdownContent({
  markdown,
  emptyLabel = "-",
}: MarkdownContentProps) {
  const source = markdown?.trim() ?? "";
  if (!source) {
    return (
      <Typography sx={{ fontSize: 14, color: "#8c8c8c" }}>{emptyLabel}</Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        urlTransform={previewUrlTransform}
        components={components}
      >
        {source}
      </ReactMarkdown>
    </Box>
  );
}
